/* Copyright (c) 2020-present CloudIO Inc */

import {
  AllDatasources,
  Atom,
  DBRow,
  IoUserPagesV,
  NewRow,
  Query,
} from '@cloudio-saas/datasource-types';
import {
  AsyncCallback,
  Callback,
  Path,
  StateXActionCaller,
  StateXCallProps,
  StateXGetter,
  StateXQuiteSetter,
  StateXRemover,
  StateXSetter,
} from '@cloudio-saas/statex';
import { AlertProps } from '@mui/lab';
import { Theme } from '@mui/material';
import { AlertDialogProps } from 'custom/AlertDialog';
import { UserInputDialogProps } from 'custom/UserInputDialog';
import type { Location } from 'history';
import { NoteAction } from 'platform/NotificationItem';
import { SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import Store from 'store/Store';
import { WebSocketProps } from 'streams/useWebSocket';
import { PubSub } from 'utils/PubSub';
import {
  Manager,
  BaseComponentItem,
  ExpressionValue,
  Item,
  ItemID,
  PropertyType,
  AliasType,
  AliasAndEmbeddedPageIdValue,
} from '../registry/Component.type';
import { AnyItem, ItemType, Page } from '../registry/ComponentRegistry';
import { Resource } from '../root/Resource';
import { SendRequest } from '../streams/useWebSocket';
import { SQLType } from 'platform/SQL';

export type DataSourceName = keyof AllDatasources;

export type MultiPostFunction = <T extends DataSourceName>(
  appUid: string,
  ds: T,
  rows: DBRow<AllDatasources[T]>[],
) => Promise<DBRow<AllDatasources[T]>[]>;

export type InsertManyFunction = <T extends DataSourceName>(
  appUid: string,
  ds: T,
  rows: NewRow<AllDatasources[T]>[],
) => Promise<DBRow<AllDatasources[T]>[]>;

export type SinglePostFunction = <T extends DataSourceName>(
  appUid: string,
  ds: T,
  row: DBRow<AllDatasources[T]>,
) => Promise<DBRow<AllDatasources[T]>>;

export type InsertOneFunction = <T extends DataSourceName>(
  appUid: string,
  ds: T,
  row: NewRow<AllDatasources[T]>,
) => Promise<DBRow<AllDatasources[T]>>;

export interface ShowMsgItem {
  pageId: string;
  appUid: string;
  itemId: string;
}

export interface ShowMsgProps {
  msg: string;
  type: AlertProps['severity'];
  timeoutMs?: number | undefined;
  action?: NoteAction;
  item?: ShowMsgItem;
}

export class AtomQueue {
  pending: Atom[] = [];

  waiting: [
    Omit<Atom, 'lastUpdateDate' | 'lastUpdatedBy'>,
    { pageUid?: string; appUid: string; roleUid?: string },
    (response: boolean) => void,
    (reason?: string) => void,
    string,
  ][] = [];

  error: Atom[] = [];
}
export interface Platform {
  navLoc?: { location: Location<unknown>; navigate: NavigateFunction };
  id: number;
  callFetch: (
    appUid: string,
    sql: string,
    update?: boolean | undefined,
  ) => Promise<SQLType>;
  getUserName(): string;
  atomQueue: AtomQueue;
  /** Current active theme object */
  theme: Theme;
  pubSub: PubSub | null;
  /** When called, the user will be signed out */
  signout: () => void;
  /** StateX Action Caller */
  caller: StateXActionCaller;
  /** StateX Getter */
  getter: StateXGetter;
  /** StateX Remover */
  remover: StateXRemover;
  /** StateX Setter */
  setter: StateXSetter;
  /** StateX Quiet Setter */
  setQuietly: StateXQuiteSetter;
  /** StateX Quiet Callback */
  quietCallback: <R>(fn: (_props: StateXCallProps) => R) => R;
  /** StateX Is Mounted */
  isStateXMounted: () => boolean;
  /** To send request to the server via. websocket.
   * @internal Do not use this from controller.
   */
  sendRequest: SendRequest;
  /** @internal Do not use this from controller. */
  addToUndo: (childPath: Path, hash: string) => void;
  /** @internal Do not use this from controller. */
  postUpdateCallbackRegister: (c: Callback) => void;
  /** @internal Do not use this from controller. */
  forceUpdater: <T = void>(callback: Callback<T>) => T;
  /** @internal Do not use this from controller. */
  stateXScheduler: () => void;
  /** @internal Do not use this from controller. */
  asyncBatchUpdater: <T = void>(callback: AsyncCallback<T>) => Promise<T>;
  /** @internal Do not use this from controller. */
  asyncForceUpdater: <T = void>(callback: AsyncCallback<T>) => Promise<T>;
  /** @internal Do not use this from controller. */
  batchUpdater: <T = void>(callback: Callback<T>) => T;
  /** @internal Do not use this from controller. */
  getGetter(): StateXGetter;
  /** @internal Do not use this from controller. */
  showMessage: (props: ShowMsgProps) => void;
  /**
   * Displays a confirmation dialog to the user
   * @param title The title to be displayed on the confirmation dialog
   * @param msg The content message to be displayed on the dialog
   * @param onOk The callback that will be called when the user click's on the OK button
   * @param onClose The callback that will be called when the user cancel's the dialog
   */
  confirmDialog: (props: AlertDialogProps) => void;
  /**
   * Displays a confirmation dialog to the user
   * @param title The title to be displayed on the confirmation dialog
   * @param msg The content message to be displayed on the dialog
   * @returns A promise that resolves with true if the user click's OK, false otherwise
   */
  confirm: (title: string, msg: string) => Promise<boolean>;
  /**
   * Displays an input dialog for the user to enter
   * @param title The title to be displayed on the user input dialog
   * @param content The content message to be displayed on the dialog
   * @param label The label text to used for the input field
   * @param placeholder The placeholder text of the input field
   * @returns A promise that resolves with the user entered value
   */
  userInput: (
    props: Omit<UserInputDialogProps, 'onClose' | 'onOk'>,
  ) => Promise<string>;
  userInputDialog: (props: UserInputDialogProps) => void;
  /**
   * Displays an error notification item to the user with the given message
   * @param msg The error message to be shown
   */
  showError: (msg: string, item?: ShowMsgItem) => void;
  /**
   * Displays a warning notification item to the user with the given message
   * @param msg The warning message to be shown
   */
  showWarning: (msg: string, item?: ShowMsgItem) => void;
  /**
   * Displays an info notification item to the user with the given message
   * @param msg The info message to be shown
   */
  showInfo: (msg: string) => void;
  /**
   * Displays a success notification item to the user with the given message
   * @param msg The success message to be shown
   */
  showSuccess: (msg: string) => void;
  /** Current active page id */
  pageId: string;
  /** Current active role uid */
  roleUid: string;
  /** @internal Do not use this from controller. */
  originalPageId: string;
  /** Current active application id */
  appUid: string;
  getManager: (itemId: string) => Manager | null;
  /**
   * Returns the runtime metadata value of the property of a given item
   * @param appUid The application id of the page
   * @param pageId The page id
   * @param itemId The item id of the component to be updated
   * @param key The property name/key
   * @returns The property value
   */
  getRuntimeProp: <T, K extends keyof T = keyof T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: K,
  ) => T[K];
  /**
   * Returns the metadata value of the property of a given item. If a runtime
   * property value exists, it will be returned, else a design time value
   * will be returned if present. Else, the default value will be returned.
   * @param appUid The application id of the page
   * @param pageId The page id
   * @param itemId The item id of the component to be updated
   * @param key The property name/key
   * @returns The property value
   */
  getItemProp: <T, K extends keyof T = keyof T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: K,
  ) => T[K];
  /**
   * Returns the design time metadata value of the property of a given item
   * @param appUid The application id of the page
   * @param pageId The page id
   * @param itemId The item id of the component to be updated
   * @param key The property name/key
   * @returns The property value
   */
  getDesignProp: <T, K extends keyof T = keyof T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: K,
  ) => T[K];
  /**
   * Updates the runtime metadata of a given component
   * @param appUid The application id of the page
   * @param pageId The page id
   * @param itemId The item id of the component to be updated
   * @param key The property name/key
   * @param value The value of the property
   */
  setRuntimeProp: <T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: keyof T,
    value: SetStateAction<T[keyof T]>,
  ) => void;
  /**
   * Updates the formula metadata of a given component
   * @param appUid The application id of the page
   * @param pageId The page id
   * @param itemId The item id of the component to be updated
   * @param key The property name/key
   * @param value The value of the property
   */
  setFormulaProp: <T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: keyof T,
    value: SetStateAction<T[keyof T]>,
  ) => void;
  /**
   * Updates the design time metadata of a given component
   * @param appUid The application id of the page
   * @param pageId The page id
   * @param itemId The item id of the component to be updated
   * @param key The property name/key
   * @param value The value of the property
   */
  setDesignProp: <T>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    key: keyof T,
    value: SetStateAction<T[keyof T]>,
  ) => void;
  replacePropertyValue: (
    page: Page,
    appUid: string,
    pageId: string,
    propertyType: PropertyType,
    oldValue: unknown,
    newValue: unknown,
  ) => Page;
  /** @internal Do not use this from controller. */
  shortenItemID: (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    donotUpdateActiveItem?: boolean | undefined,
  ) => string;
  /** @internal Do not use this from controller. */
  getShortenedId: (appUid: string, pageId: ItemID, itemId: ItemID) => string;
  /** @internal Do not use this from controller. */
  updateItemID: (
    appUid: string,
    pageId: ItemID,
    oldItemId: ItemID,
    newItemId: ItemID,
    donotUpdateActiveItem?: boolean | undefined,
  ) => string;
  getChildren(appUid: string, pageId: ItemID, itemId: ItemID): ItemID[];
  /**
   * Returns an item's metadata
   * @param appUid Application ID
   * @param pageId Page ID
   * @param itemId Item ID
   * @returns the item metadata from the state if the page is already navigated by
   * the user, else returns undefined.
   */
  getItem(appUid: string, pageId: ItemID, itemId: ItemID): AnyItem | undefined;
  /**
   * Returns the page metadata
   * @param appUid Application ID
   * @param pageId Page ID
   * @returns the page metadata from the state if the page is already navigated by
   * the user, else returns undefined.
   */
  getPage(appUid: string, pageId: ItemID): Page;
  /** @internal Do not use this from controller. */
  isDeveloperMode(pageId: ItemID): boolean;
  /** @internal Do not use this from controller. */
  hasChildrenOfTypeById<T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    type: T,
  ): boolean;
  /** @internal Do not use this from controller. */
  getChildrenOfTypeById<T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    type: T,
  ): Item<T>[];
  /** @internal Do not use this from controller. */
  getChildrenItems: <R extends BaseComponentItem>(
    appUid: string,
    pageId: ItemID,
    page: Page,
    item: AnyItem,
    filter: (child: AnyItem) => boolean,
  ) => R[];
  /** @internal Do not use this from controller. */
  getChildrenItemsById<R extends AnyItem>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    filter: (child: AnyItem) => boolean,
  ): R[];
  /** @internal Do not use this from controller. */
  findParent(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    filter: (parent: AnyItem) => boolean,
  ): AnyItem | null;
  /** @internal Do not use this from controller. */
  findParentOfType<T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    parentType: T,
  ): Item<T> | null;
  /** @internal Do not use this from controller. */
  getChildrenOfType: <T extends ItemType>(
    appUid: string,
    pageId: ItemID,
    page: Page,
    item: AnyItem,
    type: T,
  ) => Item<T>[];
  /** @internal Do not use this from controller. */
  getStore: <S>(
    appUid: string,
    pageId: ItemID,
    alias: string,
    silent?: boolean,
  ) => Store<S> | null;
  /** @internal Do not use this from controller. */
  getParentStore: <S>(
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
  ) => Store<S> | null;
  /** @internal Do not use this from controller. */
  evalString: (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string | undefined,
  ) => string | null;
  /** @internal Do not use this from controller. */
  evalIfExpressionString: (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: ExpressionValue | undefined,
  ) => string | null;
  /** @internal Do not use this from controller. */
  evalBoolean: (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string | undefined,
  ) => boolean;
  /** @internal Do not use this from controller. */
  evalNumber: (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string | undefined,
  ) => number | null;
  /** @internal Do not use this from controller. */
  evalUnknown: (
    appUid: string,
    pageId: ItemID,
    itemId: ItemID,
    property: string,
    props: Record<string, unknown>,
    expression?: string | undefined,
  ) => unknown;
  /** @internal Do not use this from controller. */
  registerAlias: (props: {
    appUid: string;
    pageId: string;
    alias: string;
    dataSource: string;
  }) => void;
  /** @internal Do not use this from controller. */
  isAliasRegistered: (props: {
    appUid: string;
    pageId: string;
    alias: string;
  }) => boolean;
  /** @internal Do not use this from controller. */
  getDataSourceName: (
    appUid: string,
    pageId: string,
    alias: string,
  ) => string | undefined;
  /** @internal Do not use this from controller. */
  getDataSources: (
    appUid: string,
    pageId: string,
  ) => Map<string, string> | undefined;
  /** @internal Do not use this from controller. */
  resource: () => Resource;
  /** @internal Do not use this from controller. */
  navigate: NavigateFunction;
  /** @internal Do not use this from controller. */
  location: Location;
  /**
   * Navigates to the given page
   * @param appUid The application id of the page
   * @param pageId The page id
   * @param params e.g. param1=abc&param2=123
   */
  goToPage: (
    appUid: string,
    pageId?: string | null,
    params?: string,
  ) => boolean;
  /** @internal Do not use this from controller. */
  getDataSourceAlias: (
    appUid: string,
    pageId: string,
    itemId: ItemID,
    aliasType: AliasType,
  ) => AliasAndEmbeddedPageIdValue | null;
  /** @internal Do not use this from controller. */
  registerLoadingIndicator: (fns: [() => void, () => void]) => void;
  /** @internal Do not use this from controller. */
  startAsyncTransition: (
    cb: () => Promise<void>,
    setPending: (pending: boolean) => void,
    context?: { appUid: string; pageId: string; itemId: string },
  ) => Promise<void>;
  /** @internal Do not use this from controller. */
  getAllAliases: (appUid: string, pageId: string) => string[];
  /** @internal Do not use this from controller. */
  resetAliases: (appUid: string, pageId: string) => void;
  /** @internal Do not use this from controller. */
  ws: WebSocketProps;
  /** @internal Do not use this from controller. */
  postAtom: (
    a: Omit<Atom, 'lastUpdateDate' | 'lastUpdatedBy' | 'id'>,
    option: { appUid: string; pageUid?: string; roleUid?: string },
  ) => Promise<boolean>;
  /**
   * Navigates to the given page link
   * @param pageLink e.g. /myapp/mypage?param1=abc&param2=123
   */
  navigateTo: (pageLink: string) => void;
  /** @internal Do not use this from controller. */
  selectItemInDesigner: (item: ShowMsgItem) => void;
  /**
   * Returns true if the current user have Developer role assigned for the given application ID.
   * @param appUid Application ID
   */
  isDeveloper: (appUid: string) => boolean;

  hasPendingAtoms: () => boolean;

  invokeCloudFunction: <I, O>(
    appUid: string,
    functionUri: string,
    data?: I,
  ) => Promise<O>;

  isRecording: () => boolean;

  recordLine: (itemId: string, message: string) => void;

  setPages: (pages: DBRow<IoUserPagesV>[]) => void;

  find: <T extends DataSourceName>(
    appUid: string,
    ds: T,
    request: Query<AllDatasources[T]>,
  ) => Promise<DBRow<AllDatasources[T]>[]>;

  findOne: <T extends DataSourceName>(
    appUid: string,
    ds: T,
    request: Query<AllDatasources[T]>,
  ) => Promise<DBRow<AllDatasources[T]> | null>;

  insertMany: InsertManyFunction;

  updateMany: MultiPostFunction;

  deleteMany: MultiPostFunction;

  insertOne: InsertOneFunction;

  updateOne: SinglePostFunction;

  deleteOne: SinglePostFunction;

  redrawCanvasGrid: (appUid: string, pageId: ItemID, itemId: ItemID) => void;

  getURLParam: (param: string) => string | null;
  getPageData: (appUid: string, pageId: ItemID, key: string) => unknown;
  setPageData: (
    appUid: string,
    pageId: ItemID,
    key: string,
    value: unknown,
  ) => void;
  getGlobalData: (key: string) => unknown;
  setGlobalData: (key: string, value: unknown) => void;
  getLocalStorageData: (appUid: string, key: string) => unknown;
  setLocalStorageData: (appUid: string, key: string, value: unknown) => void;
  setFocus: (itemId: ItemID) => void;
  invokeTrigger: (
    appUid: string,
    pageId: string,
    targetItemId: string,
  ) => Promise<boolean>;
  /**
   * This clear app connection cache,Returns true if the cache is cleared with out any error.
   * @param appUid Application ID
   */
  clearAppConn: (platform: Platform, appUid: string) => void;
}

export const PlatformName = 'Platform';
